import React, { useState, useCallback } from "react";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref, get } from "firebase/database";
import { firestore, functions, database } from "../firebaseConfig";
import { SOCIAL_PLATFORMS, computeHasEmail } from '../shared/enums';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import { costOf } from '../config/creditPlans';
import { lookupBlacklistForEmails, isEmailBlacklisted } from '../shared/blacklistUtils';
import { logContactActivity } from '../shared/contactActivityLog';
import useResearchAccess from '../hooks/useResearchAccess';
import ResearchLockedNotice from './ResearchLockedNotice';

const socialPlatforms = SOCIAL_PLATFORMS;

const DM_ADD_FORM_DEFAULT = { isOpen: false, name: '', role: '', email: '', phone: '', socials: {} };
const DM_FINDER_COST = costOf('dmEmailFinder');
const DM_DEEP_FINDER_COST = costOf('dmEmailFinderDeep');

const EMAIL_SOURCE_LABELS = {
  smtp_pattern: 'Pattern erraten + per SMTP verifiziert',
  o365: 'Microsoft 365 Tenant hat Account bestätigt',
  domain_pattern_learning: 'Pattern aus öffentlichen Firmenseiten gelernt',
  osint: 'Online via Google-Suche gefunden',
  department: 'Generisches Postfach (Abteilung)',
};

function formatFoundAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return null;
  }
}

function EmailSourcePopover({ source, onClose }) {
  if (!source) return null;
  const label = EMAIL_SOURCE_LABELS[source.method] || source.method || 'Unbekannt';
  const when = formatFoundAt(source.foundAt);
  return (
    <div className="dm-source-popover" onClick={(e) => e.stopPropagation()}>
      <div className="dm-source-popover-header">
        <i className="fa fa-shield-alt" />
        <span>Datenquelle</span>
        <button className="dm-source-popover-close" onClick={onClose} aria-label="Close">
          <i className="fa fa-times" />
        </button>
      </div>
      <div className="dm-source-popover-body">
        <div className="dm-source-row"><span className="dm-source-key">Methode</span><span className="dm-source-val">{label}</span></div>

        {source.method === 'osint' && (
          <>
            {source.query && (
              <div className="dm-source-row"><span className="dm-source-key">Such-Query</span><code className="dm-source-code">{source.query}</code></div>
            )}
            {source.resultUrl && (
              <div className="dm-source-row"><span className="dm-source-key">Trefferseite</span>
                <a href={source.resultUrl} target="_blank" rel="noopener noreferrer" className="dm-source-link">
                  {source.resultTitle || source.resultUrl}
                </a>
              </div>
            )}
            {source.snippet && (
              <div className="dm-source-row"><span className="dm-source-key">Snippet</span><span className="dm-source-snippet">{source.snippet}</span></div>
            )}
          </>
        )}

        {source.method === 'domain_pattern_learning' && (
          <>
            {source.learnedPattern && (
              <div className="dm-source-row"><span className="dm-source-key">Gelerntes Pattern</span><code className="dm-source-code">{source.learnedPattern}</code></div>
            )}
            {Array.isArray(source.learnedFromUrls) && source.learnedFromUrls.length > 0 && (
              <div className="dm-source-row"><span className="dm-source-key">Gelernt von</span>
                <div className="dm-source-urls">
                  {source.learnedFromUrls.map((u, i) => (
                    <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="dm-source-link">{u}</a>
                  ))}
                </div>
              </div>
            )}
            {source.sampleCount > 0 && (
              <div className="dm-source-row"><span className="dm-source-key">Sample-Mails</span><span className="dm-source-val">{source.sampleCount}</span></div>
            )}
          </>
        )}

        {source.method === 'o365' && (
          <>
            {source.pattern && (
              <div className="dm-source-row"><span className="dm-source-key">Pattern</span><code className="dm-source-code">{source.pattern}</code></div>
            )}
            {source.tenantDomain && (
              <div className="dm-source-row"><span className="dm-source-key">M365 Tenant</span><span className="dm-source-val">{source.tenantDomain}</span></div>
            )}
            <div className="dm-source-row"><span className="dm-source-key">SMTP-Recheck</span><span className="dm-source-val">{source.smtpVerified ? 'OK' : 'nicht bestätigt'}</span></div>
          </>
        )}

        {source.method === 'smtp_pattern' && (
          <>
            {source.pattern && (
              <div className="dm-source-row"><span className="dm-source-key">Pattern</span><code className="dm-source-code">{source.pattern}</code></div>
            )}
            {source.stage && (
              <div className="dm-source-row"><span className="dm-source-key">Stage</span><span className="dm-source-val">{source.stage}</span></div>
            )}
          </>
        )}

        {source.method === 'department' && source.pattern && (
          <div className="dm-source-row"><span className="dm-source-key">Postfach</span><code className="dm-source-code">{source.pattern}@</code></div>
        )}

        {when && (
          <div className="dm-source-row"><span className="dm-source-key">Gefunden am</span><span className="dm-source-val">{when}</span></div>
        )}
      </div>
    </div>
  );
}

const DecisionMakersModal = ({ isOpen, contactData, decisionMakers: initialDMs, onClose, userId, onContactUpdate }) => {
  const { research, researchLoading, researchContext } = useResearchAccess();
  const [decisionMakers, setDecisionMakers] = useState(initialDMs || []);
  const [dmAddForm, setDmAddForm] = useState(DM_ADD_FORM_DEFAULT);
  const [saving, setSaving] = useState(false);

  // Email Finder state
  const [finderRunning, setFinderRunning] = useState(false);
  const [finderProgress, setFinderProgress] = useState(null);
  const [singleSearchIdx, setSingleSearchIdx] = useState(null);
  const [deepSearchIdx, setDeepSearchIdx] = useState(null);
  const [sourceOpenIdx, setSourceOpenIdx] = useState(null);
  const [blacklistRemoval, setBlacklistRemoval] = useState({ blockedEmails: [], removing: false });
  // Global target-market setting — localizes DM finder search/patterns per country.
  const [targetCountry, setTargetCountry] = useState('auto');

  React.useEffect(() => {
    if (!isOpen || !userId) return;
    let active = true;
    get(ref(database, `/World/${userId}/settings/spySettings`))
      .then((snap) => {
        if (active && snap.exists()) setTargetCountry(snap.val()?.advancedSettings?.targetCountry || 'auto');
      })
      .catch(() => {});
    return () => { active = false; };
  }, [isOpen, userId]);

  React.useEffect(() => {
    if (isOpen) {
      setDecisionMakers(initialDMs || []);
      setDmAddForm(DM_ADD_FORM_DEFAULT);
      setFinderRunning(false);
      setFinderProgress(null);
      setSingleSearchIdx(null);
      setDeepSearchIdx(null);
      setSourceOpenIdx(null);
      setBlacklistRemoval({ blockedEmails: [], removing: false });
    }
  }, [isOpen, initialDMs]);

  const saveDMs = async (updatedList, extraFields = null) => {
    if (!userId || !contactData.id) return;
    setSaving(true);
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contactData.id}`);
      const payload = { lead_decision_makers: JSON.stringify(updatedList), ...(extraFields || {}) };
      // Keep the denormalized has_email index in sync: adding/removing a DM email
      // can flip whether this contact is writable, which outreach auto-pools and
      // flow `has_email` conditions rely on.
      payload.has_email = computeHasEmail({ ...contactData, ...payload });
      await updateDoc(contactRef, payload);
      setDecisionMakers(updatedList);
      // Keep the in-memory contact in sync so a follow-up search in the same
      // session sees the freshly stored company email (e.g. lead_email).
      if (extraFields) Object.assign(contactData, extraFields);
      onContactUpdate(contactData.id, payload);
    } catch (error) {
      console.error('Failed to save decision makers:', error);
    } finally {
      setSaving(false);
    }
  };

  // Merge a generic company mailbox (info@/kontakt@ …) returned by the finder
  // into the contact's general email field. It is appended (never overwrites an
  // existing address) and de-duplicated. Returns the new lead_email string, or
  // null when nothing changed. The Normal-Mode sender picks it up automatically
  // via getAllContactEmails (source 'general').
  const buildLeadEmailWithCompany = (companyEmail) => {
    if (!companyEmail || !companyEmail.includes('@')) return null;
    const existing = (contactData.lead_email || '')
      .split(/[,;\s]+/)
      .map(e => e.trim())
      .filter(Boolean);
    if (existing.some(e => e.toLowerCase() === companyEmail.toLowerCase())) return null;
    return [...existing, companyEmail].join(', ');
  };

  const handleAdd = async () => {
    if (!dmAddForm.name.trim()) return;
    const cleanSocials = {};
    Object.entries(dmAddForm.socials).forEach(([k, v]) => {
      if (v && v.trim()) cleanSocials[k] = v.trim();
    });
    const newDm = {
      name: dmAddForm.name.trim(),
      role: dmAddForm.role.trim() || null,
      email: dmAddForm.email.trim() || null,
      phone: dmAddForm.phone.trim() || null,
      socials: Object.keys(cleanSocials).length > 0 ? cleanSocials : null,
    };
    await saveDMs([...decisionMakers, newDm]);
    logContactActivity(firestore, userId, contactData.id, {
      type: 'edited',
      title: 'Decision maker added',
      detail: [newDm.name, newDm.role].filter(Boolean).join(' · '),
    });
    setDmAddForm(DM_ADD_FORM_DEFAULT);
  };

  const handleDelete = async (idx) => {
    const removed = decisionMakers[idx];
    await saveDMs(decisionMakers.filter((_, i) => i !== idx));
    logContactActivity(firestore, userId, contactData.id, {
      type: 'edited',
      title: 'Decision maker removed',
      detail: removed ? [removed.name, removed.role].filter(Boolean).join(' · ') : '',
    });
  };

  const handleClose = () => {
    setDmAddForm(DM_ADD_FORM_DEFAULT);
    onClose();
  };

  // ── Email Finder Logic ──
  const dmsNeedingEmail = decisionMakers.filter(dm =>
    dm.name && dm.name.trim().length >= 3 && (!dm.email || !dm.email.includes('@'))
  );
  // Der DM Email Finder ist ein eigenständig lizenziertes Research-Tool. Die
  // Lizenz hängt an der aktiven World, gilt also auch für Team-Mitarbeiter.
  const dmFinderLocked = !researchLoading && research.hasDmEmailFinder !== true;
  const canRunFinder = dmsNeedingEmail.length > 0 && !finderRunning && !dmFinderLocked;

  const runEmailFinder = useCallback(async (targetDmIndex = null) => {
    if (finderRunning || dmFinderLocked) return;

    const dmsToSearch = targetDmIndex !== null
      ? [decisionMakers[targetDmIndex]]
      : decisionMakers.filter(dm => dm.name && dm.name.trim().length >= 3 && (!dm.email || !dm.email.includes('@')));

    if (dmsToSearch.length === 0) return;

    setFinderRunning(true);
    if (targetDmIndex !== null) setSingleSearchIdx(targetDmIndex);
    setFinderProgress({ phase: 'deducting', message: 'Deducting credits...' });

    try {
      const deductCreditsFn = httpsCallable(functions, 'deductCredits');
      await deductCreditsFn({
        action: 'dm_email_finder',
        amount: DM_FINDER_COST,
        worldId: userId,
        metadata: { contactId: contactData.id, dmCount: dmsToSearch.length }
      });
    } catch (err) {
      const msg = err?.message?.includes('resource-exhausted') ? 'Not enough credits' : 'Credit deduction failed';
      setFinderProgress({ phase: 'error', message: msg });
      setFinderRunning(false);
      setSingleSearchIdx(null);
      return;
    }

    setFinderProgress({ phase: 'searching', message: `Searching ${dmsToSearch.length} decision maker${dmsToSearch.length > 1 ? 's' : ''}...` });

    try {
      const targetNames = targetDmIndex !== null ? dmsToSearch.map(d => d.name) : undefined;
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.dmEmailFinder(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contacts: [{
            contactId: contactData.id,
            decisionMakers: decisionMakers,
            targetDmNames: targetNames,
            website: contactData.lead_website || '',
            existingEmail: contactData.lead_email || '',
            businessName: contactData.business_name || '',
            linkedinUrl: contactData.lead_linkedin || '',
            country: contactData.lead_country || '',
            targetCountry,
          }]
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (!data.success || !data.results?.[0]) throw new Error('Invalid response');

      const contactResult = data.results[0];

      // Re-check the blacklist before assigning newly-discovered emails:
      // these contacts often had no email at import time, so the import
      // blacklist guard never ran on them. Uses the scalable batch lookup
      // so the cost stays bounded by the number of discovered emails (~1-5)
      // instead of the blacklist size (potentially 30k+ entries).
      const candidateEmails = (contactResult.results || [])
        .filter(r => r.status === 'found' && r.email)
        .map(r => r.email);
      const blacklistSets = candidateEmails.length > 0
        ? await lookupBlacklistForEmails(candidateEmails, userId)
        : { emails: new Set(), domains: new Set() };

      const updatedDMs = [...decisionMakers];
      let appliedCount = 0;
      let blockedCount = 0;
      const blockedEmails = []; // [{ name, email }]

      for (const result of contactResult.results) {
        if (result.status === 'found' && result.email) {
          const dmIdx = updatedDMs.findIndex(dm => dm.name === result.name && (!dm.email || !dm.email.includes('@')));
          if (dmIdx === -1) continue;

          if (isEmailBlacklisted(result.email, blacklistSets)) {
            updatedDMs[dmIdx] = {
              ...updatedDMs[dmIdx],
              emailSearched: true,
              emailNotFound: true,
              emailNote: 'Blocked by blacklist',
            };
            blockedCount++;
            blockedEmails.push({ name: result.name, email: result.email });
            continue;
          }

          const isVerified = result.emailVerified !== false;
          updatedDMs[dmIdx] = {
            ...updatedDMs[dmIdx],
            email: result.email,
            emailVerified: isVerified,
            emailSearched: true,
            emailNotFound: false,
            source: result.discoveryMethod === 'o365' ? 'o365' : 'dm_finder',
            ...(result.emailSource && { emailSource: result.emailSource }),
            ...(result.discoveryMethod === 'o365' && !isVerified && { emailNote: 'O365 confirmed — catch-all domain' }),
            ...(result.discoveryMethod === 'catch_all_pattern' && { emailNote: 'Catch-all — aus Firmen-Muster abgeleitet (unverifiziert)' }),
            ...(result.isGeneric && { emailNote: 'Allgemeines Postfach (Catch-all) — kein persönliches verifizierbar' }),
          };
          appliedCount++;
        } else if (result.status === 'not_found') {
          const dmIdx = updatedDMs.findIndex(dm => dm.name === result.name);
          if (dmIdx !== -1) {
            updatedDMs[dmIdx] = { ...updatedDMs[dmIdx], emailSearched: true, emailNotFound: true };
          }
        } else if (result.status === 'catch_all') {
          const dmIdx = updatedDMs.findIndex(dm => dm.name === result.name);
          if (dmIdx !== -1) {
            updatedDMs[dmIdx] = { ...updatedDMs[dmIdx], emailSearched: true, emailNote: 'catch-all domain' };
          }
        }
      }

      const newLeadEmail = buildLeadEmailWithCompany(contactResult.companyEmail);
      await saveDMs(updatedDMs, newLeadEmail ? { lead_email: newLeadEmail } : null);

      const isCatchAll = contactResult.isCatchAll;
      const companyNote = newLeadEmail ? ` — company email saved (${contactResult.companyEmail})` : '';
      const foundMsg = appliedCount > 0
        ? `Found ${appliedCount} email${appliedCount > 1 ? 's' : ''}${blockedCount > 0 ? ` (${blockedCount} blocked by blacklist)` : ''}${companyNote}`
        : blockedCount > 0
          ? `${blockedCount} email${blockedCount > 1 ? 's' : ''} blocked by blacklist${companyNote}`
          : newLeadEmail
            ? `Company email saved (${contactResult.companyEmail})`
            : isCatchAll
              ? 'Catch-all domain — emails cannot be verified'
              : 'No emails found';

      setFinderProgress({ phase: 'done', message: foundMsg, found: appliedCount, isCatchAll });
      logContactActivity(firestore, userId, contactData.id, {
        type: 'dm_emails',
        title: 'DM email finder',
        detail: `${foundMsg} · ${dmsToSearch.length} searched`,
      });
      if (blockedEmails.length > 0) {
        setBlacklistRemoval(prev => ({
          blockedEmails: [...prev.blockedEmails, ...blockedEmails],
          removing: false,
        }));
      }
    } catch (err) {
      console.error('[DM-EMAIL-FINDER] Error:', err);
      setFinderProgress({ phase: 'error', message: 'Search failed — credits refunded' });
      try {
        const deductCreditsFn = httpsCallable(functions, 'deductCredits');
        await deductCreditsFn({ action: 'dm_email_finder_refund', amount: -DM_FINDER_COST, worldId: userId });
      } catch (_) {}
    } finally {
      setFinderRunning(false);
      setSingleSearchIdx(null);
    }
  }, [decisionMakers, finderRunning, dmFinderLocked, userId, contactData]);

  const runDeepEmailFinder = useCallback(async (dmIndex) => {
    if (finderRunning || dmFinderLocked) return;
    const dm = decisionMakers[dmIndex];
    if (!dm || !dm.name || dm.name.trim().length < 3) return;

    setFinderRunning(true);
    setDeepSearchIdx(dmIndex);
    setFinderProgress({ phase: 'deducting', message: 'Deducting credits...' });

    try {
      const deductCreditsFn = httpsCallable(functions, 'deductCredits');
      await deductCreditsFn({
        action: 'dm_email_finder_deep',
        amount: DM_DEEP_FINDER_COST,
        worldId: userId,
        metadata: { contactId: contactData.id, dmName: dm.name, type: 'deep' }
      });
    } catch (err) {
      const msg = err?.message?.includes('resource-exhausted') ? 'Not enough credits' : 'Credit deduction failed';
      setFinderProgress({ phase: 'error', message: msg });
      setFinderRunning(false);
      setDeepSearchIdx(null);
      return;
    }

    setFinderProgress({ phase: 'searching', message: `Deep search for ${dm.name}...` });

    try {
      const updatedDMs = [...decisionMakers];
      updatedDMs[dmIndex] = { ...updatedDMs[dmIndex], emailNotFound: false, emailSearched: false };

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.dmEmailFinder(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contacts: [{
            contactId: contactData.id,
            decisionMakers: updatedDMs,
            targetDmNames: [dm.name],
            website: contactData.lead_website || '',
            existingEmail: contactData.lead_email || '',
            businessName: contactData.business_name || '',
            linkedinUrl: contactData.lead_linkedin || '',
            deepSearch: true,
            country: contactData.lead_country || '',
            targetCountry,
          }]
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (!data.success || !data.results?.[0]) throw new Error('Invalid response');

      const contactResult = data.results[0];
      let applied = false;
      let blocked = false;
      const blockedEmailsDeep = []; // [{ name, email }]

      // Same blacklist re-check as the regular finder — deep search hits
      // a wider set of patterns, so the chance of producing a blacklisted
      // address is higher, not lower. Scalable batch lookup keeps the cost
      // proportional to the discovered emails, not to the blacklist size.
      const candidateEmailsDeep = (contactResult.results || [])
        .filter(r => r.status === 'found' && r.email)
        .map(r => r.email);
      const blacklistSets = candidateEmailsDeep.length > 0
        ? await lookupBlacklistForEmails(candidateEmailsDeep, userId)
        : { emails: new Set(), domains: new Set() };

      for (const result of contactResult.results) {
        if (result.status === 'found' && result.email) {
          if (isEmailBlacklisted(result.email, blacklistSets)) {
            updatedDMs[dmIndex] = {
              ...updatedDMs[dmIndex],
              emailSearched: true,
              emailNotFound: true,
              emailNote: 'Blocked by blacklist',
            };
            blocked = true;
            blockedEmailsDeep.push({ name: result.name, email: result.email });
            continue;
          }
          const isVerified = result.emailVerified !== false;
          updatedDMs[dmIndex] = {
            ...updatedDMs[dmIndex],
            email: result.email,
            emailVerified: isVerified,
            emailSearched: true,
            emailNotFound: false,
            source: result.discoveryMethod === 'o365' ? 'o365' : 'dm_finder',
            ...(result.emailSource && { emailSource: result.emailSource }),
            ...(result.discoveryMethod === 'o365' && !isVerified && { emailNote: 'O365 confirmed — catch-all domain' }),
            ...(result.discoveryMethod === 'catch_all_pattern' && { emailNote: 'Catch-all — aus Firmen-Muster abgeleitet (unverifiziert)' }),
            ...(result.isGeneric && { emailNote: 'Allgemeines Postfach (Catch-all) — kein persönliches verifizierbar' }),
          };
          applied = true;
        } else if (result.status === 'not_found') {
          updatedDMs[dmIndex] = { ...updatedDMs[dmIndex], emailSearched: true, emailNotFound: true };
        } else if (result.status === 'catch_all') {
          updatedDMs[dmIndex] = { ...updatedDMs[dmIndex], emailSearched: true, emailNote: 'catch-all domain' };
        }
      }

      const newLeadEmailDeep = buildLeadEmailWithCompany(contactResult.companyEmail);
      await saveDMs(updatedDMs, newLeadEmailDeep ? { lead_email: newLeadEmailDeep } : null);

      const isCatchAll = contactResult.isCatchAll;
      const companyNote = newLeadEmailDeep ? ` — company email saved (${contactResult.companyEmail})` : '';
      const foundMsg = applied
        ? `Email found for ${dm.name}${companyNote}`
        : blocked
          ? `Email for ${dm.name} blocked by blacklist${companyNote}`
          : newLeadEmailDeep
            ? `Company email saved (${contactResult.companyEmail})`
            : isCatchAll
              ? 'Catch-all domain — emails cannot be verified'
              : `No email found (10 patterns tested)`;

      setFinderProgress({ phase: 'done', message: foundMsg, found: applied ? 1 : 0, isCatchAll });
      logContactActivity(firestore, userId, contactData.id, {
        type: 'dm_emails',
        title: 'DM email finder · deep search',
        detail: foundMsg,
      });
      if (blockedEmailsDeep.length > 0) {
        setBlacklistRemoval(prev => ({
          blockedEmails: [...prev.blockedEmails, ...blockedEmailsDeep],
          removing: false,
        }));
      }
    } catch (err) {
      console.error('[DM-DEEP-FINDER] Error:', err);
      setFinderProgress({ phase: 'error', message: 'Deep search failed — credits refunded' });
      try {
        const deductCreditsFn = httpsCallable(functions, 'deductCredits');
        await deductCreditsFn({ action: 'dm_email_finder_deep_refund', amount: -DM_DEEP_FINDER_COST, worldId: userId });
      } catch (_) {}
    } finally {
      setFinderRunning(false);
      setDeepSearchIdx(null);
    }
  }, [decisionMakers, finderRunning, dmFinderLocked, userId, contactData]);

  // One-click contact removal — the orange "Remove Contact" button + the
  // banner above it ARE the confirmation. No extra browser dialog.
  const handleRemoveBlacklistedContact = async () => {
    if (blacklistRemoval.removing || !contactData?.id) return;
    setBlacklistRemoval(prev => ({ ...prev, removing: true }));
    try {
      await deleteDoc(doc(firestore, `World/${userId}/db/${contactData.id}`));
      if (onContactUpdate) onContactUpdate(contactData.id, { _deleted: true });
      onClose();
    } catch (err) {
      console.error('[DM-MODAL] Failed to remove blacklisted contact:', err);
      setBlacklistRemoval(prev => ({ ...prev, removing: false }));
    }
  };

  const getCardClass = (dm) => {
    let cls = 'dm-card';
    if (dm.emailNotFound && !dm.email) cls += ' dm-card--not-found';
    if (dm.emailValidationFailed) cls += ' dm-card--invalid';
    if (dm.emailVerified) cls += ' dm-card--verified';
    return cls;
  };

  if (!isOpen || !contactData) return null;

  return (
    <div className="decision-makers-modal-overlay" onClick={handleClose}>
      <div className="decision-makers-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dm-modal-header">
          <div className="dm-modal-title">
            <i className="fa fa-user-tie"></i>
            <div>
              <h3>Decision Makers</h3>
              <span className="dm-company-name">{contactData.business_name || 'Company'}</span>
            </div>
          </div>
          <button className="dm-modal-close" onClick={handleClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        <div className="dm-modal-content">
          {/* Email Finder Banner */}
          {dmsNeedingEmail.length > 0 && !dmAddForm.isOpen && dmFinderLocked && (
            <div className="dm-finder-banner">
              <div className="dm-finder-banner-left">
                <i className="fa fa-lock" />
                <ResearchLockedNotice
                  featureName="DM Email Finder"
                  accountEmail={researchContext.accountEmail}
                  worldId={researchContext.worldId}
                />
              </div>
            </div>
          )}

          {dmsNeedingEmail.length > 0 && !dmAddForm.isOpen && !dmFinderLocked && (
            <div className={`dm-finder-banner ${finderProgress?.phase === 'done' ? (finderProgress.found > 0 ? 'success' : 'empty') : ''}`}>
              <div className="dm-finder-banner-left">
                <i className={`fa ${finderRunning ? 'fa-spinner fa-spin' : finderProgress?.phase === 'done' ? (finderProgress.found > 0 ? 'fa-check-circle' : 'fa-info-circle') : 'fa-crosshairs'}`}></i>
                <div>
                  <span className="dm-finder-banner-title">
                    {finderProgress?.phase === 'done'
                      ? finderProgress.message
                      : finderRunning
                        ? finderProgress?.message || 'Searching...'
                        : 'Email Finder'}
                  </span>
                  {!finderRunning && !finderProgress && (
                    <span className="dm-finder-banner-sub">
                      {dmsNeedingEmail.length} DM{dmsNeedingEmail.length > 1 ? 's' : ''} without email &middot; {DM_FINDER_COST} credits
                    </span>
                  )}
                </div>
              </div>
              {!finderRunning && finderProgress?.phase !== 'done' && (
                <button className="dm-finder-btn" onClick={() => runEmailFinder()} disabled={!canRunFinder}>
                  <i className="fa fa-search"></i>
                  Find All
                </button>
              )}
            </div>
          )}

          {/* Error banner */}
          {finderProgress?.phase === 'error' && (
            <div className="dm-finder-banner error">
              <div className="dm-finder-banner-left">
                <i className="fa fa-exclamation-triangle"></i>
                <span className="dm-finder-banner-title">{finderProgress.message}</span>
              </div>
            </div>
          )}

          {/* Blacklist hit banner — appears when discovered emails match
              the blacklist. Offers one-click contact removal. */}
          {blacklistRemoval.blockedEmails.length > 0 && (
            <div className="dm-blacklist-banner">
              <div className="dm-blacklist-banner-left">
                <i className="fa fa-ban"></i>
                <div>
                  <span className="dm-blacklist-banner-title">
                    1 Contact → Blacklist
                  </span>
                  <span className="dm-blacklist-banner-sub">
                    {blacklistRemoval.blockedEmails.length} discovered email{blacklistRemoval.blockedEmails.length === 1 ? '' : 's'} on your blacklist:
                    {' '}
                    {blacklistRemoval.blockedEmails.slice(0, 2).map(b => b.email).join(', ')}
                    {blacklistRemoval.blockedEmails.length > 2 ? ` (+${blacklistRemoval.blockedEmails.length - 2})` : ''}
                  </span>
                </div>
              </div>
              <button
                className="dm-blacklist-remove-btn"
                onClick={handleRemoveBlacklistedContact}
                disabled={blacklistRemoval.removing}
              >
                {blacklistRemoval.removing
                  ? <><i className="fa fa-spinner fa-spin"></i> Removing...</>
                  : <><i className="fa fa-trash"></i> Remove Contact</>}
              </button>
            </div>
          )}

          {decisionMakers.length === 0 && !dmAddForm.isOpen ? (
            <div className="dm-empty-state">
              <i className="fa fa-user-slash"></i>
              <h4>No Decision Makers Found</h4>
              <p>Add decision makers manually or run Web Research to discover them automatically.</p>
            </div>
          ) : (
            <div className="dm-list">
              {decisionMakers.map((dm, idx) => (
                <div key={idx} className={getCardClass(dm)}>
                  <div className="dm-card-avatar">
                    {dm.emailNotFound && !dm.email
                      ? <i className="fa fa-user-slash"></i>
                      : <i className="fa fa-user-tie"></i>}
                  </div>
                  <div className="dm-card-info">
                    <h4 className="dm-name">
                      {dm.name || 'Unknown'}
                      {dm.source && <span className="dm-source-badge" title={`Source: ${dm.source}`}>{dm.source === 'linkedin' ? 'LI' : dm.source === 'grok' ? 'AI' : 'Web'}</span>}
                    </h4>
                    <span className="dm-role">{dm.role || dm.position || 'Executive'}</span>
                    {dm.email ? (
                      <div className="dm-contact-row">
                        <a href={`mailto:${dm.email}`} className={`dm-contact ${dm.emailValidationFailed ? 'dm-contact--invalid' : ''}`}>
                          <i className="fa fa-envelope"></i>
                          {dm.email}
                          {dm.emailValidationFailed
                            ? <span className="dm-invalid-badge" title={dm.emailNote || 'Email found invalid'}><i className="fa fa-exclamation-triangle"></i> invalid</span>
                            : dm.emailVerified && <span className="dm-verified-badge" title="Email verified"><i className="fa fa-check-circle"></i></span>}
                        </a>
                        {dm.emailValidationFailed && dm.emailNote && (
                          <span className="dm-invalid-note">{dm.emailNote}</span>
                        )}
                        {dm.emailSource && (
                          <div className="dm-source-wrap">
                            <button
                              type="button"
                              className="dm-source-btn"
                              title="Datenquelle anzeigen"
                              onClick={(e) => { e.stopPropagation(); setSourceOpenIdx(sourceOpenIdx === idx ? null : idx); }}
                            >
                              <i className="fa fa-info-circle" />
                            </button>
                            {sourceOpenIdx === idx && (
                              <EmailSourcePopover
                                source={dm.emailSource}
                                onClose={() => setSourceOpenIdx(null)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ) : dm.emailNotFound ? (
                      <span className="dm-contact dm-contact--not-found">
                        <i className="fa fa-envelope-open"></i>
                        No email found
                        <button
                          className="dm-retry-btn dm-retry-deep"
                          onClick={(e) => {
                            e.stopPropagation();
                            runDeepEmailFinder(idx);
                          }}
                          disabled={finderRunning || saving || dmFinderLocked}
                          title={dmFinderLocked ? 'DM Email Finder is not available for your account' : `Deep Search — tests 10 email patterns (${DM_DEEP_FINDER_COST} credits)`}
                        >
                          {deepSearchIdx === idx
                            ? <i className="fa fa-spinner fa-spin"></i>
                            : <><i className="fa fa-redo"></i><span className="dm-deep-cost">{DM_DEEP_FINDER_COST}</span></>}
                        </button>
                      </span>
                    ) : (
                      <button
                        className="dm-find-email-btn"
                        onClick={() => runEmailFinder(idx)}
                        disabled={finderRunning || dmFinderLocked || !dm.name || dm.name.trim().length < 3}
                        title={dmFinderLocked ? 'DM Email Finder is not available for your account' : `Find email for ${dm.name}`}
                      >
                        {singleSearchIdx === idx
                          ? <><i className="fa fa-spinner fa-spin"></i> Searching...</>
                          : <><i className="fa fa-crosshairs"></i> Find Email</>}
                      </button>
                    )}
                    {dm.phone && (
                      <a href={`tel:${dm.phone}`} className="dm-contact">
                        <i className="fa fa-phone"></i>
                        {dm.phone}
                      </a>
                    )}
                  </div>
                  <div className="dm-card-actions">
                    {dm.socials && Object.entries(dm.socials).map(([platform, url]) => {
                      const p = socialPlatforms.find(sp => sp.key === platform);
                      if (!p || !url) return null;
                      return (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dm-social-btn"
                          style={{ background: p.color }}
                          title={p.label}
                        >
                          <i className={p.icon}></i>
                        </a>
                      );
                    })}
                    <button
                      className="dm-delete-btn"
                      title="Remove"
                      disabled={saving}
                      onClick={() => handleDelete(idx)}
                    >
                      <i className="fa fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {dmAddForm.isOpen && (
            <div className="dm-add-form">
              <div className="dm-add-form-fields">
                <div className="dm-field-row">
                  <div className="dm-field">
                    <label>Name *</label>
                    <input
                      type="text"
                      placeholder="John Smith"
                      value={dmAddForm.name}
                      onChange={e => setDmAddForm(prev => ({ ...prev, name: e.target.value }))}
                      autoFocus
                    />
                  </div>
                  <div className="dm-field">
                    <label>Role / Position</label>
                    <input
                      type="text"
                      placeholder="CEO, Head of Sales..."
                      value={dmAddForm.role}
                      onChange={e => setDmAddForm(prev => ({ ...prev, role: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="dm-field-row">
                  <div className="dm-field">
                    <label>Email</label>
                    <input
                      type="email"
                      placeholder="john@company.com"
                      value={dmAddForm.email}
                      onChange={e => setDmAddForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className="dm-field">
                    <label>Phone</label>
                    <input
                      type="tel"
                      placeholder="+1 234 567 890"
                      value={dmAddForm.phone}
                      onChange={e => setDmAddForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="dm-socials-section">
                  <label className="dm-socials-label">Social Media</label>
                  <div className="dm-socials-chips">
                    {socialPlatforms.map(p => {
                      const isActive = dmAddForm.socials[p.key] !== undefined;
                      return (
                        <button
                          key={p.key}
                          type="button"
                          className={`dm-social-chip ${isActive ? 'active' : ''}`}
                          style={isActive ? { background: p.color, borderColor: p.color, color: '#fff' } : {}}
                          onClick={() => setDmAddForm(prev => {
                            const next = { ...prev, socials: { ...prev.socials } };
                            if (isActive) { delete next.socials[p.key]; } else { next.socials[p.key] = ''; }
                            return next;
                          })}
                        >
                          <i className={p.icon}></i>
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  {Object.keys(dmAddForm.socials).length > 0 && (
                    <div className="dm-social-url-fields">
                      {Object.keys(dmAddForm.socials).map(key => {
                        const p = socialPlatforms.find(sp => sp.key === key);
                        if (!p) return null;
                        return (
                          <div key={key} className="dm-field dm-social-url-field">
                            <label><i className={p.icon} style={{ color: p.color, marginRight: 6 }}></i>{p.label}</label>
                            <input
                              type="url"
                              placeholder={p.placeholder}
                              value={dmAddForm.socials[key]}
                              onChange={e => setDmAddForm(prev => ({ ...prev, socials: { ...prev.socials, [key]: e.target.value } }))}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="dm-modal-footer">
          <span className="dm-count-info">
            <i className="fa fa-info-circle"></i>
            {decisionMakers.length} Decision Maker{decisionMakers.length !== 1 ? 's' : ''}
          </span>
          <div className="dm-footer-actions">
            {dmAddForm.isOpen ? (
              <>
                <button className="dm-form-cancel" onClick={() => setDmAddForm(DM_ADD_FORM_DEFAULT)}>
                  Cancel
                </button>
                <button className="dm-form-save" disabled={!dmAddForm.name.trim() || saving} onClick={handleAdd}>
                  {saving ? <><i className="fa fa-spinner fa-spin"></i> Saving...</> : <><i className="fa fa-check"></i> Add</>}
                </button>
              </>
            ) : (
              <button className="dm-add-btn" onClick={() => setDmAddForm(prev => ({ ...prev, isOpen: true }))}>
                <i className="fa fa-plus"></i>
                Add
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionMakersModal;
